/**
 * LaCaisse.jsx — La Caisse MVP: Payment Recording
 * Rently.ma · Morocco Fleet OS
 *
 * Real payment recording against the live payments table.
 * No mock data. No fallback writes. Void = soft-delete (deleted_at).
 *
 * States: LOADING → CONNECTED | EMPTY | ERROR
 * - LOADING: skeleton shimmer, no data
 * - CONNECTED: real data from API, form enabled
 * - EMPTY: API reachable, zero payments, form enabled
 * - ERROR: API unreachable, form disabled, zero rows
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMAD, formatTime } from '../utils/formatters';
import {
  PageHeader, Badge, StatCard, Card,
  GlassModal,
} from '../components/ui.jsx';
import { caisse as caisseApi, rentals as rentalsApi } from '../services/api.js';

// ── Method display config ────────────────────────────────────────────
const METHOD_MAP = {
  cash:          { labelKey: 'caisse.method_cash',     color: 'var(--green)' },
  card:          { labelKey: 'caisse.method_card',     color: 'var(--orange)' },
  bank_transfer: { labelKey: 'caisse.method_transfer', color: 'var(--blue)' },
  cheque:        { labelKey: 'caisse.method_cheque',   color: 'var(--text-secondary)' },
};

// ── Shared styles ────────────────────────────────────────────────────
const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'var(--raised)',
  border: '1px solid var(--border-medium)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text)', fontSize: 13,
  fontFamily: 'inherit', outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
  textTransform: 'uppercase', letterSpacing: '0.06em',
  marginBottom: 4, display: 'block',
};

// ── Add Payment Modal ────────────────────────────────────────────────
function AddPaymentModal({ onClose, onSuccess, rentalsList, disabled }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    rental_id: '', amount: '', method: 'cash', note: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async () => {
    setError('');
    if (!form.rental_id) { setError(t('caisse.err_rental')); return; }
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { setError(t('caisse.err_amount')); return; }

    setSubmitting(true);
    try {
      await caisseApi.record({
        rental_id: form.rental_id,
        amount: amt,
        method: form.method,
        note: form.note.trim() || null,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || t('system.api_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlassModal open title={t('caisse.modal_add_title')} subtitle={t('caisse.mvp_notice')} onClose={onClose} width={480}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
        {/* Rental selector */}
        <div>
          <label style={labelStyle}>{t('caisse.rental_link_label')} *</label>
          <select value={form.rental_id} onChange={e => set('rental_id', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }} disabled={disabled}>
            <option value="">{t('caisse.select_rental')}</option>
            {rentalsList.map(r => (
              <option key={r.id} value={r.id}>
                {r.plate_number} — {r.client_name} ({r.status})
              </option>
            ))}
          </select>
        </div>

        {/* Amount */}
        <div>
          <label style={labelStyle}>{t('caisse.amount_label')} *</label>
          <input type="number" value={form.amount} onChange={e => set('amount', e.target.value)}
            placeholder="0" min="0.01" step="0.01" disabled={disabled}
            style={{
              ...inputStyle,
              fontFamily: 'var(--font-mono)',
              fontSize: 18, fontWeight: 700,
            }} />
        </div>

        {/* Method */}
        <div>
          <label style={labelStyle}>{t('caisse.method_label')} *</label>
          <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
            {['cash', 'card', 'bank_transfer'].map(m => (
              <button key={m} onClick={() => set('method', m)} disabled={disabled} style={{
                flex: 1, padding: '10px 0',
                background: form.method === m ? 'rgba(52,84,179,0.12)' : 'transparent',
                border: `1px solid ${form.method === m ? 'var(--gold)' : 'var(--border)'}`,
                borderRadius: 'var(--radius-sm)',
                color: form.method === m ? 'var(--gold)' : 'var(--text-secondary)',
                fontSize: 12, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
              }}>
                {t(METHOD_MAP[m].labelKey)}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div>
          <label style={labelStyle}>{t('caisse.note_label')}</label>
          <input value={form.note} onChange={e => set('note', e.target.value)}
            placeholder={t('caisse.note_placeholder')} disabled={disabled}
            style={inputStyle} />
        </div>

        {error && (
          <div style={{ color: 'var(--red)', fontSize: 13, padding: '8px 12px',
            background: 'rgba(248,113,113,0.08)', borderRadius: 'var(--radius-sm)' }}>
            {error}
          </div>
        )}
      </div>

      <div style={{
        padding: 'var(--space-lg) var(--space-xl)',
        borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)',
      }}>
        <button onClick={onClose} style={{
          padding: '10px 20px', background: 'transparent',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
        }}>{t('common.cancel')}</button>
        <button onClick={handleSubmit} disabled={submitting || disabled} style={{
          padding: '10px 24px',
          background: (submitting || disabled) ? 'var(--text-muted)' : 'var(--gold)',
          color: 'var(--bg)', border: 'none',
          borderRadius: 'var(--radius-sm)',
          fontSize: 13, fontWeight: 700,
          cursor: (submitting || disabled) ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit', letterSpacing: '0.02em',
        }}>
          {submitting ? t('caisse.saving') : t('caisse.add_payment')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Void Confirm Modal ───────────────────────────────────────────────
function VoidModal({ payment, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleVoid = async () => {
    setError('');
    if (!reason.trim()) { setError(t('caisse.void_reason_required')); return; }
    setSubmitting(true);
    try {
      await caisseApi.voidPayment(payment.id, { reason: reason.trim() });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || t('system.api_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GlassModal open title={t('caisse.void_payment')} onClose={onClose} width={420}>
      <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {t('caisse.void_confirm_msg')}
          <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--red)', fontSize: 18 }}>
            {formatMAD(payment.amount_mad)} MAD
          </div>
        </div>
        <div>
          <label style={labelStyle}>{t('caisse.void_reason_prompt')} *</label>
          <input value={reason} onChange={e => setReason(e.target.value)}
            placeholder={t('caisse.void_reason_placeholder')}
            style={inputStyle} />
        </div>
        {error && (
          <div style={{ color: 'var(--red)', fontSize: 13, padding: '8px 12px',
            background: 'rgba(248,113,113,0.08)', borderRadius: 'var(--radius-sm)' }}>
            {error}
          </div>
        )}
      </div>
      <div style={{
        padding: 'var(--space-lg) var(--space-xl)',
        borderTop: '1px solid var(--border)',
        display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)',
      }}>
        <button onClick={onClose} style={{
          padding: '10px 20px', background: 'transparent',
          border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
          color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
        }}>{t('common.cancel')}</button>
        <button onClick={handleVoid} disabled={submitting} style={{
          padding: '10px 24px',
          background: submitting ? 'var(--text-muted)' : 'var(--red)',
          color: '#fff', border: 'none',
          borderRadius: 'var(--radius-sm)',
          fontSize: 13, fontWeight: 700,
          cursor: submitting ? 'not-allowed' : 'pointer',
          fontFamily: 'inherit',
        }}>
          {submitting ? t('common.processing') : t('caisse.void_payment')}
        </button>
      </div>
    </GlassModal>
  );
}

// ── Main Component ───────────────────────────────────────────────────
export default function LaCaisse() {
  const { t } = useTranslation();

  // State
  const [pageState, setPageState] = useState('loading'); // loading | connected | empty | error
  const [payments, setPayments] = useState([]);
  const [dailyTotal, setDailyTotal] = useState({ total_mad: 0, count: 0 });
  const [rentalsList, setRentalsList] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const isApiUp = pageState === 'connected' || pageState === 'empty';

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [paymentsRes, todayRes, rentalsRes] = await Promise.all([
        caisseApi.payments(),
        caisseApi.today(),
        rentalsApi.list(100),
      ]);

      const paymentList = paymentsRes?.payments || [];
      setPayments(paymentList);
      setDailyTotal(todayRes || { total_mad: 0, count: 0 });
      setRentalsList(rentalsRes?.rentals || []);
      setPageState(paymentList.length > 0 ? 'connected' : 'empty');
      setErrorMsg('');
    } catch (err) {
      setPageState('error');
      setErrorMsg(err.message || t('system.api_error'));
      setPayments([]);
      setDailyTotal({ total_mad: 0, count: 0 });
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Method breakdown (from payments list, recorded only)
  const methodBreakdown = {};
  payments.filter(p => p.status === 'recorded').forEach(p => {
    const m = p.method || 'cash';
    methodBreakdown[m] = (methodBreakdown[m] || 0) + (p.amount_mad || 0);
  });

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      {/* Header */}
      <PageHeader
        title={t('caisse.title')}
        subtitle={t('caisse.subtitle')}
        action={
          <button onClick={() => setShowAdd(true)} disabled={!isApiUp} style={{
            padding: '10px 20px', height: 44,
            background: isApiUp ? 'var(--gold)' : 'var(--text-muted)',
            color: 'var(--bg)',
            border: 'none', borderRadius: 'var(--radius-sm)',
            fontSize: 13, fontWeight: 700,
            cursor: isApiUp ? 'pointer' : 'not-allowed',
            fontFamily: 'inherit', letterSpacing: '0.02em',
            whiteSpace: 'nowrap',
          }}>
            + {t('caisse.add_payment')}
          </button>
        }
      />

      {/* Error Banner */}
      {pageState === 'error' && (
        <div style={{
          background: 'rgba(248,113,113,0.08)', color: 'var(--red)',
          padding: '12px 20px', borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--space-lg)', fontWeight: 600,
          textAlign: 'center', fontSize: 14,
          border: '1px solid rgba(248,113,113,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12,
        }}>
          <span>{t('system.api_error')}</span>
          <button onClick={fetchData} style={{
            padding: '6px 16px', background: 'var(--red)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius-sm)',
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>↻ {t('caisse.retry')}</button>
        </div>
      )}

      {/* Loading skeleton */}
      {pageState === 'loading' && (
        <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
          {[0,1,2].map(i => (
            <div key={i} style={{
              height: 100, background: 'var(--card)', borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              animation: 'shimmer 1.5s ease-in-out infinite',
            }} />
          ))}
        </div>
      )}

      {/* Stat Cards — only when API responded */}
      {(pageState === 'connected' || pageState === 'empty') && (
        <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
          <StatCard
            label={t('caisse.revenue_today')}
            value={`${formatMAD(dailyTotal.total_mad)} MAD`}
            index={0}
          />
          <StatCard
            label={t('caisse.transactions_today')}
            value={`${dailyTotal.count}`}
            index={1}
          />
          <StatCard
            label={t('caisse.method_cash')}
            value={`${formatMAD(methodBreakdown.cash || 0)} MAD`}
            index={2}
          />
        </div>
      )}

      {/* Empty state */}
      {pageState === 'empty' && (
        <Card style={{ textAlign: 'center', padding: 'var(--space-3xl) var(--space-xl)' }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
            {t('caisse.no_payments_today')}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('caisse.empty_hint')}
          </div>
        </Card>
      )}

      {/* Payments Table */}
      {payments.length > 0 && (
        <div className="responsive-table">
          <div style={{
            padding: 'var(--space-lg) var(--space-xl)',
            borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              {t('caisse.recent_title')}
            </span>
            <span style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
              textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {payments.length} {t('caisse.movements')}
            </span>
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-body)' }}>
            <thead>
              <tr>
                {[t('caisse.col_time'), t('caisse.col_rental'), t('caisse.method_label'), t('caisse.amount_label'), t('common.status'), t('caisse.col_actions')].map(h => (
                  <th key={h} style={{
                    padding: '12px 16px',
                    textAlign: (h === t('caisse.amount_label')) ? 'right' : 'left',
                    fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
                    letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 600,
                    borderBottom: '1px solid var(--border)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {payments.map((p, idx) => {
                const isVoided = p.status === 'voided';
                const mCfg = METHOD_MAP[p.method] || METHOD_MAP.cash;
                return (
                  <tr key={p.id} style={{
                    borderBottom: '1px solid var(--border)',
                    opacity: isVoided ? 0.6 : 1,
                    transition: 'background var(--duration-fast) var(--ease-smooth)',
                    animation: `scaleIn 300ms var(--ease-smooth) both`,
                    animationDelay: `${idx * 40}ms`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    {/* Time */}
                    <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 12 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                        {formatTime(p.created_at)}
                      </div>
                    </td>
                    {/* Rental info */}
                    <td style={{ padding: '12px 16px', color: 'var(--text)' }}>
                      <div style={{ fontWeight: 500 }}>
                        {p.client_name || '—'}
                      </div>
                      {p.plate_number && (
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: 11 }}>
                          {p.plate_number}
                        </span>
                      )}
                      {p.note && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{p.note}</div>
                      )}
                    </td>
                    {/* Method */}
                    <td style={{ padding: '12px 16px' }}>
                      <Badge label={t(mCfg.labelKey)} variant="neutral" />
                    </td>
                    {/* Amount */}
                    <td style={{
                      padding: '12px 16px', textAlign: 'right',
                      fontFamily: 'var(--font-mono)', fontWeight: 700,
                      color: isVoided ? 'var(--red)' : 'var(--green)',
                      textDecoration: isVoided ? 'line-through' : 'none',
                    }}>
                      {formatMAD(p.amount_mad)} MAD
                    </td>
                    {/* Status */}
                    <td style={{ padding: '12px 16px' }}>
                      {isVoided ? (
                        <span title={p.void_reason || ''}>
                          <Badge label={t('caisse.status_voided')} variant="danger" />
                        </span>
                      ) : (
                        <Badge label={t('caisse.status_recorded')} variant="success" />
                      )}
                    </td>
                    {/* Actions */}
                    <td style={{ padding: '12px 16px' }}>
                      {!isVoided && isApiUp && (
                        <button onClick={() => setVoidTarget(p)} style={{
                          padding: '4px 12px', fontSize: 11, fontWeight: 600,
                          background: 'rgba(248,113,113,0.08)',
                          color: 'var(--red)',
                          border: '1px solid rgba(248,113,113,0.2)',
                          borderRadius: 'var(--radius-sm)',
                          cursor: 'pointer', fontFamily: 'inherit',
                        }}>
                          {t('caisse.void_payment')}
                        </button>
                      )}
                      {isVoided && p.void_reason && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                          {p.void_reason}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Daily Summary by Method */}
      {isApiUp && (
        <Card style={{ marginTop: 'var(--space-xl)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-lg)' }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{t('caisse.daily_summary')}</div>
              <div style={{ fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginTop: 2,
                textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {t('caisse.daily_breakdown')}
              </div>
            </div>
            <div style={{
              fontSize: 22, fontWeight: 700,
              fontFamily: 'var(--font-mono)', color: 'var(--gold)',
            }}>
              {formatMAD(dailyTotal.total_mad)} MAD
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            {['cash', 'card', 'bank_transfer'].map(m => {
              const val = methodBreakdown[m] || 0;
              const maxVal = Math.max(...Object.values(methodBreakdown), 1);
              const cfg = METHOD_MAP[m];
              return (
                <div key={m}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: cfg.color }} />
                      {t(cfg.labelKey)}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>
                      {formatMAD(val)} MAD
                    </span>
                  </div>
                  <div style={{
                    height: 8, background: 'rgba(0,0,0,0.06)',
                    borderRadius: 4, overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 4, background: cfg.color,
                      width: `${(val / maxVal) * 100}%`,
                      transition: 'width 0.6s var(--ease-smooth)',
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Summary bar */}
      {isApiUp && (
        <div style={{
          marginTop: 'var(--space-lg)',
          padding: 'var(--space-md) var(--space-lg)',
          background: 'var(--card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontSize: 13, color: 'var(--text-secondary)',
        }}>
          <span>
            <strong style={{ color: 'var(--text)' }}>{dailyTotal.count}</strong> {t('caisse.payments_today')}
          </span>
          <span style={{
            fontFamily: 'var(--font-mono)', fontWeight: 700,
            color: 'var(--gold)', fontSize: 15,
          }}>
            {t('caisse.balance')} : {formatMAD(dailyTotal.total_mad)} MAD
          </span>
        </div>
      )}

      {/* Modals */}
      {showAdd && (
        <AddPaymentModal
          onClose={() => setShowAdd(false)}
          onSuccess={fetchData}
          rentalsList={rentalsList}
          disabled={!isApiUp}
        />
      )}
      {voidTarget && (
        <VoidModal
          payment={voidTarget}
          onClose={() => setVoidTarget(null)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
}
